import { Injectable, NgZone } from '@angular/core';
import { FirebaseProviderService } from '@project/providers';
import { monitorLogger } from '../monitor/monitor-logger';
import { IFeatureService } from './_index';
import { defer, from, Observable, of, TimeoutError } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import {
  WEBSOCKET_AVAILABLE_PROVIDERS,
  WebsocketAvailableProvider,
  WebsocketLifecycleService,
} from '@core/realtime/websocket';
import * as firebase from 'firebase/app';
import { environment } from '@env';

@Injectable({
  providedIn: 'root',
})
export class FirestoreWebsocketTriggerService implements IFeatureService {
  private static readonly INITIAL_PROVIDER_READ_TIMEOUT_MS = 2000;
  private static readonly DEFAULT_PROVIDER = WEBSOCKET_AVAILABLE_PROVIDERS.SOCKET_IO;

  private isInitialized = false;
  private firestoreUnsubscribe: (() => void) | null = null;

  constructor(
    private firebaseProvider: FirebaseProviderService,
    private websocketLifecycleService: WebsocketLifecycleService,
    private ngZone: NgZone,
  ) {}

  public initialise(): Observable<void> {
    if (this.isInitialized) return of(null);

    this.startListeningToFirestoreWebsocketDefinition();

    this.isInitialized = true;

    return of(null);
  }

  public destroy(): Observable<void> {
    this.firestoreUnsubscribe?.();
    this.firestoreUnsubscribe = null;
    this.isInitialized = false;
    return of(null);
  }

  /**
   * Reads `socket-config/{env}` once before any connection is made and applies the configured provider.
   *
   * - If a valid provider is configured, it is applied and automatic provider updates are disabled.
   * - If the document is missing, the provider is invalid, or the read errors or times out, the default
   *   provider is applied and automatic provider updates remain enabled.
   *
   * Always emits and completes, so it never blocks initialisation.
   *
   * The read and its timeout are subscribed outside the Angular zone, since pending tasks created inside
   * the zone keep the application unstable. Applying the provider, emitting and completing re-enter the zone.
   */
  public resolveInitialProvider(): Observable<void> {
    const read$ = defer(() => from(this.getSocketConfigDocRef().get())).pipe(
      timeout(FirestoreWebsocketTriggerService.INITIAL_PROVIDER_READ_TIMEOUT_MS),
      map((snapshot) => this.resolveProviderFromSnapshot(snapshot)),
      catchError((error: unknown) => {
        const reason = error instanceof TimeoutError ? 'timeout' : 'error';
        console.warn(`[FirestoreTrigger] Failed to read socket-config (${reason}). Using default provider.`, error);
        return of(null);
      }),
      tap((provider) => this.ngZone.run(() => this.applyInitialProvider(provider))),
      map(() => undefined),
      catchError(() => of(undefined)),
    );

    return new Observable<void>((subscriber) =>
      this.ngZone.runOutsideAngular(() =>
        read$.subscribe({
          next: (value) => this.ngZone.run(() => subscriber.next(value)),
          complete: () => this.ngZone.run(() => subscriber.complete()),
        }),
      ),
    );
  }

  private getSocketConfigDocRef(): firebase.firestore.DocumentReference {
    const currEnv = environment.currentEnvironment ?? 'dev';
    return this.firebaseProvider.getFirestore().collection('socket-config').doc(currEnv);
  }

  private resolveProviderFromSnapshot(
    snapshot: firebase.firestore.DocumentSnapshot,
  ): WebsocketAvailableProvider | null {
    if (!snapshot.exists) {
      console.warn('[FirestoreTrigger] Document not found. Using default provider.');
      return null;
    }

    const normalizedProvider = this.getNormalizedProvider(snapshot);
    const provider = this.toAvailableProvider(normalizedProvider);

    if (!provider) {
      console.warn(
        `[FirestoreTrigger] Invalid provider in socket-config: ${
          normalizedProvider || 'empty'
        }. Using default provider.`,
      );
    }

    return provider;
  }

  private applyInitialProvider(provider: WebsocketAvailableProvider | null): void {
    if (provider) {
      this.forceProvider(provider);
      return;
    }

    this.websocketLifecycleService.setSocketProviderUpdateEnabled(true);
    this.websocketLifecycleService.setProvider(FirestoreWebsocketTriggerService.DEFAULT_PROVIDER);
  }

  /**
   * Circuit breaker for the WebSocket provider configuration.
   *
   * This logic checks whether there is a forced provider version defined
   * (stored externally, e.g., in Firebase).
   *
   * Behavior:
   *
   * - If `enforcedProvider` has a value:
   *   It means an incident occurred and we are intentionally locking
   *   the WebSocket provider to a specific implementation/version.
   *   In this scenario:
   *     - The provider is explicitly set.
   *     - Automatic provider updates are disabled.
   *
   * - If `enforcedProvider` is NOT defined:
   *   It means the system should operate normally.
   *   In this scenario:
   *     - No provider is forcibly overridden.
   *     - Automatic provider updates remain enabled.
   *
   * This acts as a safety switch (circuit breaker) to prevent
   * automatic provider changes during instability or outages.
   */

  private startListeningToFirestoreWebsocketDefinition() {
    if (this.firestoreUnsubscribe) this.firestoreUnsubscribe();

    const docRef = this.getSocketConfigDocRef();

    this.ngZone.runOutsideAngular(() => {
      this.firestoreUnsubscribe = docRef.onSnapshot(this.handleSnapshot, this.handleSnapshotError);
    });
  }

  private handleSnapshot = (snapshot: firebase.firestore.DocumentSnapshot): void => {
    if (!snapshot.exists) {
      console.warn('[FirestoreTrigger] Document not found.');
      this.websocketLifecycleService.setSocketProviderUpdateEnabled(true);
      return;
    }

    const normalizedProvider = this.getNormalizedProvider(snapshot);

    if (!normalizedProvider) {
      this.websocketLifecycleService.setSocketProviderUpdateEnabled(true);
      return;
    }

    const enforcedProvider = this.toAvailableProvider(normalizedProvider);

    if (!enforcedProvider) {
      console.warn(`[FirestoreTrigger] Invalid provider in socket-config: ${normalizedProvider}. Ignoring it.`);
      this.websocketLifecycleService.setSocketProviderUpdateEnabled(true);
      return;
    }

    this.forceProvider(enforcedProvider);
  };

  private handleSnapshotError = (error: Error): void => {
    this.ngZone.run(() => {
      console.error('[FirestoreTrigger] Error listening to Firestore:', error);
      monitorLogger.app.errorGeneral(error);
    });
  };

  private getNormalizedProvider(snapshot: firebase.firestore.DocumentSnapshot): string {
    return (snapshot.data()?.['provider'] || '').toString().trim().toUpperCase();
  }

  private toAvailableProvider(normalizedProvider: string): WebsocketAvailableProvider | null {
    return Object.values(WEBSOCKET_AVAILABLE_PROVIDERS).find((provider) => provider === normalizedProvider) ?? null;
  }

  private forceProvider(enforcedProvider: string): void {
    this.ngZone.run(() => {
      this.websocketLifecycleService.setSocketProviderUpdateEnabled(true);
      this.websocketLifecycleService.setProvider(enforcedProvider as WebsocketAvailableProvider);
      this.websocketLifecycleService.setSocketProviderUpdateEnabled(false);
    });
  }
}
